import { CPText } from "shared/components/base";
import { CPTextProps } from "shared/components/types";
import { getFieldError } from "shared/helpers";
import * as React from "react";

interface CPErrorMessageProps extends Omit<CPTextProps, "children"> {
  /** The error text to display. */
  errorMsg?: string;
  /** Explicitly toggle visibility. When omitted, the component is visible only when `errorMsg` or a resolved field error is a non-empty string. */
  showError?: boolean;
  /** When `true`, renders the message with a red background and white text instead of red text on a transparent background. */
  filled?: boolean;
  /** Raw API error response `{ errors: [{ propertyName, errorMessage }] }`. Used with `propertyName` to resolve a field-level error. */
  error?: any;
  /** Field name to look up in `error.errors`. Matched case-insensitively. */
  propertyName?: string;
}

/**
 * Inline error message. Renders `null` when there is nothing to show. Visibility is controlled by
 * `showError` (explicit) or by whether `errorMsg` is a non-empty string (implicit).
 *
 * @example
 * ```tsx
 * // Implicit — visible only when errorMsg has content
 * <CPErrorMessage errorMsg={errors.email} />
 *
 * // Explicit — always visible when showError is true
 * <CPErrorMessage errorMsg="Something went wrong." showError={submitFailed} />
 *
 * // Filled style — red background / white text
 * <CPErrorMessage errorMsg="Access denied." filled />
 * ```
 */
const CPErrorMessage: React.FC<CPErrorMessageProps> = (props) => {
  const resolvedMsg =
    props.errorMsg ?? getFieldError(props.error, props.propertyName ?? "");

  const showError = () => {
    if (props.showError != null) {
      return props.showError;
    }

    return resolvedMsg != null && resolvedMsg.length > 0;
  };

  return showError() ? (
    <CPText
      {...props}
      color={props.filled ? "white" : "danger"}
      style={{
        backgroundColor: props.filled ? "#ce3434" : "inherit",
        padding: "5px",
        textTransform: "capitalize",
        marginTop: "5px",
        marginBottom: "5px",
      }}
    >
      {resolvedMsg}
    </CPText>
  ) : null;
};

export default CPErrorMessage;
